import { io, type Socket } from "socket.io-client";
import "./styles.css";

interface PlayerState {
  id: string;
  name: string;
  seat: number;
  wins: number;
  winnings: string;
  hasGuessed: boolean;
  isMe: boolean;
}

interface LastSpin {
  roundNo: number;
  resultNumber: number;
  potBefore: string;
  hadWinner: boolean;
  createdAt: string;
  guesses: Array<{
    playerId: string;
    name: string;
    number: number;
  }>;
  winners: Array<{
    playerId: string;
    name: string;
    share: string;
  }>;
}

interface GameState {
  room: {
    code: string;
    status: "active" | "finished";
    prizeLabel: string;
    roundPrizeAmount: string;
    potAmount: string;
    rolloverCount: number;
    roundNo: number;
    createdAt: string;
  };
  players: PlayerState[];
  myGuess: number | null;
  canSpin: boolean;
  lastSpin: LastSpin | null;
}

interface Session {
  roomCode: string;
  playerToken: string;
}

interface ApiErrorShape {
  error?: {
    code?: string;
    message?: string;
  };
}

const SESSION_KEY = "qqnq_session_v1";
const TUTORIAL_SEEN_KEY = "qqnq_tutorial_seen_v1";

function requiredElement<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Elemento obrigatório não encontrado: ${selector}`);
  }
  return element;
}

const lobbyView = requiredElement<HTMLElement>("#lobbyView");
const gameView = requiredElement<HTMLElement>("#gameView");
const createRoomForm = requiredElement<HTMLFormElement>("#createRoomForm");
const joinRoomForm = requiredElement<HTMLFormElement>("#joinRoomForm");
const createName = requiredElement<HTMLInputElement>("#createName");
const prizeLabel = requiredElement<HTMLInputElement>("#prizeLabel");
const roundPrizeAmount = requiredElement<HTMLInputElement>("#roundPrizeAmount");
const joinCode = requiredElement<HTMLInputElement>("#joinCode");
const joinName = requiredElement<HTMLInputElement>("#joinName");
const roomCodeText = requiredElement<HTMLElement>("#roomCodeText");
const copyInviteButton = requiredElement<HTMLButtonElement>("#copyInviteButton");
const connectionDot = requiredElement<HTMLElement>("#connectionDot");
const connectionText = requiredElement<HTMLElement>("#connectionText");
const leaveRoomButton = requiredElement<HTMLButtonElement>("#leaveRoomButton");
const potValue = requiredElement<HTMLElement>("#potValue");
const prizeText = requiredElement<HTMLElement>("#prizeText");
const roundBadge = requiredElement<HTMLElement>("#roundBadge");
const rolloverBadge = requiredElement<HTMLElement>("#rolloverBadge");
const playerCountBadge = requiredElement<HTMLElement>("#playerCountBadge");
const playersList = requiredElement<HTMLElement>("#playersList");
const guessGrid = requiredElement<HTMLElement>("#guessGrid");
const guessStatus = requiredElement<HTMLElement>("#guessStatus");
const spinButton = requiredElement<HTMLButtonElement>("#spinButton");
const spinHint = requiredElement<HTMLElement>("#spinHint");
const wheel = requiredElement<HTMLElement>("#wheel");
const wheelLabels = requiredElement<HTMLElement>("#wheelLabels");
const lastSpinEmpty = requiredElement<HTMLElement>("#lastSpinEmpty");
const lastSpinContent = requiredElement<HTMLElement>("#lastSpinContent");
const lastResultNumber = requiredElement<HTMLElement>("#lastResultNumber");
const lastRoundText = requiredElement<HTMLElement>("#lastRoundText");
const lastGuesses = requiredElement<HTMLElement>("#lastGuesses");
const lastWinnerText = requiredElement<HTMLElement>("#lastWinnerText");
const toast = requiredElement<HTMLElement>("#toast");
const tutorialButton = requiredElement<HTMLButtonElement>("#tutorialButton");
const tutorialModal = requiredElement<HTMLElement>("#tutorialModal");
const closeTutorialButton = requiredElement<HTMLButtonElement>("#closeTutorialButton");
const startPlayingButton = requiredElement<HTMLButtonElement>("#startPlayingButton");
const resultModal = requiredElement<HTMLElement>("#resultModal");
const modalNumber = requiredElement<HTMLElement>("#modalNumber");
const modalTitle = requiredElement<HTMLElement>("#modalTitle");
const modalText = requiredElement<HTMLElement>("#modalText");
const closeModalButton = requiredElement<HTMLButtonElement>("#closeModalButton");

let session: Session | null = loadSession();
let gameState: GameState | null = null;
let realtimeSocket: Socket | null = null;
let toastTimer: number | null = null;
let wheelRotation = 0;
let lastAnimatedSpinRound = 0;
let spinPending = false;

function money(value: string): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value));
}

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.roomCode === "string" &&
      typeof parsed.playerToken === "string"
    ) {
      return {
        roomCode: parsed.roomCode,
        playerToken: parsed.playerToken
      };
    }
  } catch {
    localStorage.removeItem(SESSION_KEY);
  }

  return null;
}

function saveSession(nextSession: Session): void {
  session = nextSession;
  localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
}

function clearSession(): void {
  session = null;
  gameState = null;
  realtimeSocket?.disconnect();
  realtimeSocket = null;
  localStorage.removeItem(SESSION_KEY);
}

async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");

  if (session?.playerToken) {
    headers.set("x-player-token", session.playerToken);
  }

  const response = await fetch(path, {
    ...options,
    headers
  });

  const payload = (await response.json().catch(() => ({}))) as T & ApiErrorShape;

  if (!response.ok) {
    throw new Error(
      payload.error?.message ?? "Não foi possível concluir a operação."
    );
  }

  return payload;
}

function openTutorial(): void {
  tutorialModal.classList.remove("hidden");
}

function closeTutorial(): void {
  tutorialModal.classList.add("hidden");
  localStorage.setItem(TUTORIAL_SEEN_KEY, "1");
}

function showToast(message: string): void {
  toast.textContent = message;
  toast.classList.remove("hidden");

  if (toastTimer !== null) {
    window.clearTimeout(toastTimer);
  }

  toastTimer = window.setTimeout(() => {
    toast.classList.add("hidden");
  }, 3200);
}

function setConnection(status: "online" | "connecting" | "offline"): void {
  connectionDot.classList.remove("online", "offline");

  if (status === "online") {
    connectionDot.classList.add("online");
    connectionText.textContent = "Tempo real conectado";
  } else if (status === "offline") {
    connectionDot.classList.add("offline");
    connectionText.textContent = "Reconectando…";
  } else {
    connectionText.textContent = "Conectando…";
  }
}

function openGameView(): void {
  lobbyView.classList.add("hidden");
  gameView.classList.remove("hidden");
}

function buildWheelLabels(): void {
  const segmentAngle = 360 / 11;
  const radiusPercent = 39;

  wheelLabels.replaceChildren();

  for (let number = 0; number <= 10; number += 1) {
    const centerAngle = number * segmentAngle + segmentAngle / 2;
    const radians = (centerAngle * Math.PI) / 180;
    const label = document.createElement("span");
    label.className = "wheel-label";
    label.textContent = String(number);
    label.style.left = `${50 + radiusPercent * Math.sin(radians)}%`;
    label.style.top = `${50 - radiusPercent * Math.cos(radians)}%`;
    wheelLabels.append(label);
  }
}

function createGuessButtons(): void {
  guessGrid.replaceChildren();

  for (let number = 0; number <= 10; number += 1) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "guess-button";
    button.textContent = String(number);
    button.dataset.number = String(number);
    button.addEventListener("click", () => void submitGuess(number));
    guessGrid.append(button);
  }
}

function renderPlayers(state: GameState): void {
  playersList.replaceChildren();
  playerCountBadge.textContent = `${state.players.length}/2`;

  for (const player of state.players) {
    const card = document.createElement("div");
    card.className = "player-card";

    const avatar = document.createElement("div");
    avatar.className = "player-avatar";
    avatar.textContent = player.name.slice(0, 1).toUpperCase();

    const info = document.createElement("div");
    const name = document.createElement("div");
    name.className = "player-name";
    name.textContent = player.isMe ? `${player.name} (você)` : player.name;

    const meta = document.createElement("div");
    meta.className = "player-meta";
    const stats =
      Number(player.winnings) > 0
        ? `${player.wins} vitória(s) • ${money(player.winnings)}`
        : `${player.wins} vitória(s)`;
    meta.textContent = stats;

    info.append(name, meta);

    const status = document.createElement("div");
    status.className = `guess-state${player.hasGuessed ? " locked" : ""}`;
    status.textContent = player.hasGuessed ? "PALPITE OK" : "ESCOLHENDO";

    card.append(avatar, info, status);
    playersList.append(card);
  }

  if (state.players.length === 1) {
    const waiting = document.createElement("div");
    waiting.className = "empty-state";
    waiting.textContent = "Aguardando seu amigo entrar com o código da sala.";
    playersList.append(waiting);
  }
}

function renderGuesses(state: GameState): void {
  const buttons = guessGrid.querySelectorAll<HTMLButtonElement>(".guess-button");

  for (const button of buttons) {
    const number = Number(button.dataset.number);
    button.classList.toggle("selected", state.myGuess === number);
    button.disabled = state.myGuess !== null || spinPending;
  }

  if (state.myGuess !== null) {
    guessStatus.textContent =
      `Palpite ${state.myGuess} confirmado. Agora aguarde o outro jogador.`;
  } else if (state.players.length < 2) {
    guessStatus.textContent =
      "Você já pode escolher seu número enquanto espera seu amigo.";
  } else {
    guessStatus.textContent = "Escolha um número de 0 a 10.";
  }
}

function renderLastSpin(state: GameState): void {
  const last = state.lastSpin;

  if (!last) {
    lastSpinEmpty.classList.remove("hidden");
    lastSpinContent.classList.add("hidden");
    return;
  }

  lastSpinEmpty.classList.add("hidden");
  lastSpinContent.classList.remove("hidden");
  lastResultNumber.textContent = String(last.resultNumber);
  lastRoundText.textContent = `Rodada ${last.roundNo} • pote ${money(last.potBefore)}`;

  lastGuesses.replaceChildren();
  for (const guess of last.guesses) {
    const row = document.createElement("div");
    row.className = "last-guess-row";

    const player = document.createElement("span");
    player.textContent = guess.name;

    const number = document.createElement("strong");
    number.textContent = String(guess.number);

    row.append(player, number);
    lastGuesses.append(row);
  }

  if (last.winners.length === 0) {
    lastWinnerText.textContent = "Ninguém acertou — o prêmio foi acumulado.";
  } else if (last.winners.length === 1) {
    const winner = last.winners[0]!;
    lastWinnerText.textContent =
      Number(winner.share) > 0
        ? `${winner.name} acertou e ganhou ${money(winner.share)}.`
        : `${winner.name} acertou e ganhou: ${state.room.prizeLabel}.`;
  } else {
    lastWinnerText.textContent =
      Number(last.potBefore) > 0
        ? `Os dois acertaram. O prêmio foi dividido: ` +
          last.winners.map((winner) => `${winner.name} ${money(winner.share)}`).join(" • ")
        : `Os dois acertaram o prêmio "${state.room.prizeLabel}".`;
  }
}

function renderState(state: GameState): void {
  roomCodeText.textContent = state.room.code;

  const hasMoney = Number(state.room.potAmount) > 0;
  potValue.textContent = hasMoney
    ? money(state.room.potAmount)
    : state.room.prizeLabel;

  prizeText.textContent = hasMoney
    ? state.room.prizeLabel
    : "Prêmio sem valor financeiro cadastrado";

  roundBadge.textContent = `Rodada ${state.room.roundNo}`;
  rolloverBadge.textContent =
    state.room.rolloverCount > 0
      ? `${state.room.rolloverCount} acúmulo(s)`
      : "Sem acúmulo";

  renderPlayers(state);
  renderGuesses(state);
  renderLastSpin(state);

  spinButton.disabled = !state.canSpin || spinPending;
  spinHint.textContent =
    state.players.length < 2
      ? "Aguardando o segundo jogador."
      : state.canSpin
        ? "Os dois palpites estão prontos. Clique no centro para girar."
        : "Aguardando os dois palpites.";
}

async function applyState(
  nextState: GameState,
  initial = false
): Promise<void> {
  gameState = nextState;
  openGameView();
  renderState(nextState);

  if (initial) {
    lastAnimatedSpinRound = nextState.lastSpin?.roundNo ?? 0;
    return;
  }

  const lastSpin = nextState.lastSpin;
  if (lastSpin && lastSpin.roundNo > lastAnimatedSpinRound) {
    lastAnimatedSpinRound = lastSpin.roundNo;
    await animateWheel(lastSpin.resultNumber);
    openResultModal(lastSpin);
  }
}

async function animateWheel(resultNumber: number): Promise<void> {
  const segmentAngle = 360 / 11;
  const target = -(resultNumber * segmentAngle + segmentAngle / 2);
  const currentModulo = ((wheelRotation % 360) + 360) % 360;
  const targetModulo = ((target % 360) + 360) % 360;
  const delta = (targetModulo - currentModulo + 360) % 360;

  wheelRotation += 5 * 360 + delta;
  wheel.style.transform = `rotate(${wheelRotation}deg)`;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, 4500);
  });
}

function openResultModal(last: LastSpin): void {
  modalNumber.textContent = String(last.resultNumber);

  if (last.winners.length === 0) {
    modalTitle.textContent = "O prêmio acumulou!";
    modalText.textContent =
      `Ninguém acertou nesta rodada. O próximo giro começa com um pote maior.`;
  } else if (last.winners.length === 1) {
    const winner = last.winners[0]!;
    modalTitle.textContent = `${winner.name} ganhou!`;
    modalText.textContent =
      Number(winner.share) > 0
        ? `Palpite certeiro. Prêmio da rodada: ${money(winner.share)}.`
        : `Palpite certeiro. O prêmio foi: ${gameState?.room.prizeLabel ?? "prêmio da rodada"}.`;
  } else {
    modalTitle.textContent = "Empate certeiro!";
    modalText.textContent =
      `Os dois escolheram o número sorteado. O prêmio foi dividido entre os dois.`;
  }

  resultModal.classList.remove("hidden");
}

async function fetchState(initial = false): Promise<void> {
  if (!session) return;

  const response = await api<{ state: GameState }>(
    `/api/rooms/${encodeURIComponent(session.roomCode)}/state`
  );
  await applyState(response.state, initial);
}

function connectRealtime(): void {
  if (!session) return;

  realtimeSocket?.disconnect();
  setConnection("connecting");

  realtimeSocket = io({
    auth: {
      roomCode: session.roomCode,
      playerToken: session.playerToken
    },
    transports: ["websocket", "polling"]
  });

  realtimeSocket.on("connect", () => {
    setConnection("online");
  });

  realtimeSocket.on("disconnect", () => {
    setConnection("offline");
  });

  realtimeSocket.on("connect_error", () => {
    setConnection("offline");
  });

  realtimeSocket.on("game:refresh", () => {
    void fetchState(false).catch((error: unknown) => {
      showToast(error instanceof Error ? error.message : "Falha ao atualizar a sala.");
    });
  });
}

async function submitGuess(number: number): Promise<void> {
  if (!session || !gameState || gameState.myGuess !== null) return;

  try {
    const response = await api<{ state: GameState }>(
      `/api/rooms/${encodeURIComponent(session.roomCode)}/guess`,
      {
        method: "POST",
        body: JSON.stringify({ number })
      }
    );

    await applyState(response.state);
    showToast(`Palpite ${number} confirmado.`);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Não foi possível confirmar.");
  }
}

async function spin(): Promise<void> {
  if (!session || !gameState?.canSpin || spinPending) return;

  spinPending = true;
  renderState(gameState);

  try {
    const response = await api<{ state: GameState }>(
      `/api/rooms/${encodeURIComponent(session.roomCode)}/spin`,
      {
        method: "POST",
        body: "{}"
      }
    );

    await applyState(response.state);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Não foi possível girar.");
  } finally {
    spinPending = false;
    if (gameState) {
      renderState(gameState);
    }
  }
}

createRoomForm.addEventListener("submit", (event) => {
  event.preventDefault();

  void (async () => {
    try {
      const response = await api<{
        roomCode: string;
        playerToken: string;
        state: GameState;
      }>("/api/rooms", {
        method: "POST",
        body: JSON.stringify({
          name: createName.value.trim(),
          prizeLabel: prizeLabel.value.trim(),
          roundPrizeAmount: Number(roundPrizeAmount.value)
        })
      });

      saveSession({
        roomCode: response.roomCode,
        playerToken: response.playerToken
      });

      await applyState(response.state, true);
      connectRealtime();
      showToast("Sala criada. Envie o convite para seu amigo.");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Não foi possível criar a sala.");
    }
  })();
});

joinRoomForm.addEventListener("submit", (event) => {
  event.preventDefault();

  void (async () => {
    const normalizedCode = joinCode.value.trim().toUpperCase();

    try {
      const response = await api<{
        roomCode: string;
        playerToken: string;
        state: GameState;
      }>(`/api/rooms/${encodeURIComponent(normalizedCode)}/join`, {
        method: "POST",
        body: JSON.stringify({
          name: joinName.value.trim()
        })
      });

      saveSession({
        roomCode: response.roomCode,
        playerToken: response.playerToken
      });

      await applyState(response.state, true);
      connectRealtime();
      showToast("Você entrou na sala.");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Não foi possível entrar na sala.");
    }
  })();
});

joinCode.addEventListener("input", () => {
  joinCode.value = joinCode.value.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6);
});

leaveRoomButton.addEventListener("click", () => {
  const confirmed = window.confirm(
    "Criar uma nova sala? A sessão desta sala será removida deste navegador."
  );

  if (!confirmed) return;

  clearSession();
  window.location.assign(`${window.location.origin}${window.location.pathname}`);
});

copyInviteButton.addEventListener("click", () => {
  if (!session) return;

  const inviteUrl = new URL(window.location.origin);
  inviteUrl.searchParams.set("room", session.roomCode);

  void navigator.clipboard
    .writeText(inviteUrl.toString())
    .then(() => showToast("Link de convite copiado."))
    .catch(() => showToast(`Código da sala: ${session?.roomCode ?? ""}`));
});

tutorialButton.addEventListener("click", openTutorial);
closeTutorialButton.addEventListener("click", closeTutorial);
startPlayingButton.addEventListener("click", closeTutorial);

tutorialModal.addEventListener("click", (event) => {
  if (event.target === tutorialModal) {
    closeTutorial();
  }
});

spinButton.addEventListener("click", () => void spin());

closeModalButton.addEventListener("click", () => {
  resultModal.classList.add("hidden");
});

resultModal.addEventListener("click", (event) => {
  if (event.target === resultModal) {
    resultModal.classList.add("hidden");
  }
});

window.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    resultModal.classList.add("hidden");
    tutorialModal.classList.add("hidden");
  }
});

async function bootstrap(): Promise<void> {
  buildWheelLabels();
  createGuessButtons();

  const roomFromUrl = new URLSearchParams(window.location.search).get("room");
  if (roomFromUrl) {
    joinCode.value = roomFromUrl.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6);
  }

  if (!session) {
    if (!localStorage.getItem(TUTORIAL_SEEN_KEY)) {
      openTutorial();
    }
    return;
  }

  try {
    await fetchState(true);
    connectRealtime();
  } catch {
    clearSession();
    lobbyView.classList.remove("hidden");
    gameView.classList.add("hidden");
    showToast("Sua sessão anterior não pôde ser restaurada.");
  }
}

void bootstrap();
